import { Fragment } from "react";

import * as utils from "@bokio/shared/utils";
import { formatDate, formatNumberCurrency } from "@bokio/shared/utils/format";
import { mergeClassNames } from "@bokio/utils/classes";

import { useDeviceQuery } from "../DeviceQuery/useDeviceQuery";
import SorterHeading from "../Sorter/SorterHeading";
import SorterHeadingMobile from "../Sorter/SorterHeadingMobile";
import { generateSummaryRow } from "./generateSummaryRow";
import Table from "./Table";
import TableExpanderButton from "./TableExpanderButton";
import TBody from "./TBody";
import Td from "./Td";
import Th from "./Th";
import THead from "./THead";
import Tr from "./Tr";

import type { SorterConfigExtended } from "../Sorter/Sorter";
import type { DateType } from "@bokio/shared/utils/format";

import * as styles from "./table.scss";

export type KeyIn<T> = keyof T;

export type KeyInObject<T> = { [key in keyof T]?: string };

interface SimpleTableConfig<T> {
	data: T[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnConfig<T, any>[];
	level: number;
	headless?: boolean;
	headerClass?: string;
	activeRowClassName?: string;
	rowTestId?: string;
	rowClassName?: string;
	rowHoverStyleEnabled?: boolean;
	summaryRow?: boolean;
	summaryRowKeys?: KeyIn<T>[];
	summaryOverride?: KeyInObject<T>;
	onRowClick?: (row: T) => void;
	onBeforeRowRender?: (next: T, prev: T | undefined, colCount: number) => React.JSX.Element | string | undefined;
	expandedChildrenTableRender?: (item: T, level: number, colCount: number) => React.JSX.Element | undefined;
	sortConfig?: SorterConfigExtended;
}

interface SimpleTableProps<T> {
	spec: SimpleTableConfig<T>;
	expanded?: T[];
	showExpandedAsActive?: boolean;
	/**
	 * Enables switching to a more readable multi-row layout on small-screens (mobile)
	 */
	enableMobileLayout?: boolean;
	/**
	 * Always apply the mobile layout regardless of viewport size.
	 * Implies enableMobileLayout.
	 */
	forceMobileLayout?: boolean;
	className?: string;
	testId?: string;
	keyGenerationFunction?: (row: T) => string;
}

export class ColumnDefinition<T, K> implements ColumnConfig<T, K> {
	constructor(
		public label: string | React.JSX.Element | undefined,
		public accessor: (item: T, index: number) => K,
		public testId?: string,
	) {
		this.renderer = val => utils.toString(val);
	}

	public renderer: (value: K, item: T, expanded: boolean, index?: number) => string | React.JSX.Element;
	public setRenderer = (
		callback: (value: K, item: T, expanded: boolean, index?: number) => string | React.JSX.Element,
	) => {
		this.renderer = callback;
		return this;
	};

	public summaryRenderer?: (value: K) => string | React.JSX.Element;
	public setSummaryRenderer = (callback: (value: K) => string | React.JSX.Element) => {
		this.summaryRenderer = callback;
		return this;
	};

	public omitInSummary?: boolean;
	public setOmitInSummary = (omit: boolean) => {
		this.omitInSummary = omit;
		return this;
	};

	public align?: Align;
	public alignOnMobile?: Align;
	public setAlign = (align: Align | undefined, alignOnMobile?: Align) => {
		this.align = align;
		this.alignOnMobile = alignOnMobile;
		return this;
	};

	public classApplier?: (value: K, item: T) => string | undefined;
	public setClassApplier = (callback: (value: K, item: T) => string | undefined) => {
		this.classApplier = callback;
		return this;
	};

	setTestId = (testId: string) => {
		this.testId = testId;
		return this;
	};

	public visibility?: Visibility[];
	public setVisibility = (visibility: Visibility[]) => {
		this.visibility = visibility;
		return this;
	};

	public sortKey?: string;
	public setSortKey = (key: string) => {
		this.sortKey = key;
		return this;
	};

	public disableMobileHeader?: boolean;
	public setDisableMobileHeader = (value: boolean) => (this.disableMobileHeader = value);
}

export class SimpleTableSpec<T extends object> implements SimpleTableConfig<T> {
	level = 1;
	headless?: boolean;
	headerClass?: string;
	onRowClick?: (row: T) => void;
	activeRowClassName?: string;
	rowClassName?: string;
	rowHoverStyleEnabled?: boolean;
	rowTestId?: string;
	summaryRow?: boolean;
	summaryRowKeys?: KeyIn<T>[];
	summaryOverride?: KeyInObject<T>;
	sortConfig?: SorterConfigExtended;

	expandedChildrenTableRender?: (item: T, level: number, colCount: number) => React.JSX.Element | undefined;
	constructor(public data: T[]) {}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnConfig<T, any>[] = [];
	onBeforeRowRender?: (next: T, prev: T | undefined, colCount: number) => React.JSX.Element | string | undefined;

	public column<K>(label: string | React.JSX.Element | undefined, accessor: (item: T) => K) {
		const col = new ColumnDefinition<T, K>(label, accessor);
		this.columns.push(col);
		return col;
	}

	public emptyColumn() {
		const col = new ColumnDefinition<T, string>("", () => "");
		this.columns.push(col);
		return col;
	}

	public expanderColumn(
		shouldShowExpander: (item: T) => boolean,
		onExpand: (item: T, expanded: boolean) => void,
		visibility?: Visibility[],
	) {
		const col = new ColumnDefinition<T, unknown>("", t => t);
		col.setClassApplier(() => styles.expanderColumn);
		col.setRenderer((val, item, expanded) =>
			shouldShowExpander(item) ? (
				<TableExpanderButton activeTrigger={expanded || false} onClick={() => onExpand && onExpand(item, !expanded)} />
			) : (
				""
			),
		);
		visibility && col.setVisibility(visibility);
		this.columns.push(col);
		return col;
	}

	public decimalColumn(
		label: string | React.JSX.Element | undefined,
		accessor: (item: T) => number | undefined,
		currency: string,
		decimals = 2,
	) {
		const col = new ColumnDefinition<T, number | undefined>(label, accessor);
		this.columns.push(col);
		col.setRenderer(value => (value === undefined ? "" : formatNumberCurrency(value, currency, decimals)));
		col.setAlign("right");
		return col;
	}

	public dateColumn(label: string | React.JSX.Element | undefined, accessor: (item: T) => DateType | undefined) {
		const col = new ColumnDefinition<T, DateType | undefined>(label, accessor);
		this.columns.push(col);
		col.setRenderer(value => (value === undefined ? "" : formatDate(value)));
		col.setClassApplier(() => styles.noBreak);
		return col;
	}

	public actionColumn(label: string | React.JSX.Element | undefined, renderer: (item: T) => React.JSX.Element) {
		const col = new ColumnDefinition<T, unknown>(label, () => ({}));
		this.columns.push(col);
		col.setRenderer((value, item) => renderer(item));
		col.setAlign("right");
		return col;
	}

	/**
	 * Render multiple action items in a single cell with a gap.
	 */
	public multiActionColumn(label: string | React.JSX.Element | undefined, renderer: (item: T) => React.JSX.Element[]) {
		return this.actionColumn(label, item => <span className={styles.multiActionColumn}>{renderer(item)}</span>);
	}

	setBeforeRowRender = (
		callback: (next: T, prev: T | undefined, colCount: number) => React.JSX.Element | string | undefined,
	) => {
		this.onBeforeRowRender = callback;
		return this;
	};

	setActiveRowClassName = (className: string) => {
		this.activeRowClassName = className;
		return this;
	};

	setRowClickHandler = (handler: (row: T) => void) => {
		this.onRowClick = handler;
	};

	setRowClassName = (className: string) => {
		this.rowClassName = className;
	};

	applyRowHoverStyle = (value: boolean) => {
		this.rowHoverStyleEnabled = value;
	};

	setRowTestId = (testId: string) => {
		this.rowTestId = testId;
	};

	setExpandedChildrenTableRender = (
		callback: (item: T, level: number, colCount: number) => React.JSX.Element | undefined,
	) => {
		this.expandedChildrenTableRender = callback;
		return this;
	};

	setHeadless = (value: boolean) => {
		this.headless = value;
		return this;
	};

	setHeaderClass = (value: string) => {
		this.headerClass = value;
		return this;
	};

	setSortConfig = (value: SorterConfigExtended) => {
		this.sortConfig = value;
		return this;
	};

	setLevel = (value: number) => {
		this.level = value;
		return this;
	};

	addSummaryRow = (value: boolean, summaryRowKeys: KeyIn<T>[], summaryOverride: KeyInObject<T>) => {
		this.summaryRow = value;
		this.summaryRowKeys = summaryRowKeys;
		this.summaryOverride = summaryOverride;
		return this;
	};
}

export type Visibility =
	| "visibleM"
	//  | "visibleLM"  todo implement this, but fix inconsistensy with containermobilemax in _base.scss first .visibleTP */
	| "visibleTP"
	| "visibleTL"
	| "visibleDN"
	| "visibleDL"
	| "hiddenM"
	//  | "hiddenLM"
	| "hiddenTP"
	| "hiddenTL"
	| "hiddenDN"
	| "hiddenDL";

export type Align = "left" | "center" | "right";

export interface ColumnConfig<T, K> {
	renderer: (value: K, item: T, expanded: boolean, index?: number) => string | React.JSX.Element;
	summaryRenderer?: (value: K) => string | React.JSX.Element;
	omitInSummary?: boolean;
	label: string | React.JSX.Element | undefined;
	sortKey?: string;
	accessor: (item: T, index: number) => K;
	align?: Align;
	alignOnMobile?: Align;
	visibility?: Visibility[];
	classApplier?: (value: K, item: T) => string | undefined;
	testId?: string;
	disableMobileHeader?: boolean;
}

const SimpleTable = <T extends object>(props: SimpleTableProps<T>) => {
	const { spec, expanded } = props;
	const { isMobile: isDeviceMobile } = useDeviceQuery();
	const isMobile = props.forceMobileLayout || isDeviceMobile;
	const useMobileLayout = props.forceMobileLayout || props.enableMobileLayout;
	let prev: T | undefined;

	const renderRow = (summary?: boolean) => (row: T, i: number) => {
		const isExpanded = (expanded && expanded.indexOf(row) !== -1) || false;
		const rows: React.JSX.Element[] = [];
		if (spec.onBeforeRowRender !== undefined) {
			const res = spec.onBeforeRowRender(row, prev, spec.columns.length);
			if (res) {
				if (typeof res === "string") {
					rows.push(
						<Tr key={"gh_" + (props.keyGenerationFunction ? props.keyGenerationFunction(row) : i)}>
							<Th isGroupHeading={true} colSpan={spec.columns.length}>
								{res}
							</Th>
						</Tr>,
					);
				} else {
					rows.push(res);
				}
			}
		}

		rows.push(
			<Tr
				isActive={props.showExpandedAsActive && isExpanded}
				level={spec.level}
				key={props.keyGenerationFunction ? props.keyGenerationFunction(row) : i}
				onClick={spec.onRowClick && (() => spec.onRowClick?.(row))}
				className={mergeClassNames(
					spec.rowClassName,
					props.showExpandedAsActive && isExpanded && spec.activeRowClassName,
					spec.rowHoverStyleEnabled && styles.hover,
					summary && styles.summaryRow,
				)}
				testId={spec.rowTestId && `${spec.rowTestId}_${i}`}
			>
				{spec.columns.map((col, j) => {
					const value = col.accessor(row, i);
					const cls = col.classApplier ? col.classApplier(value, row) : undefined;
					const cellClass = mergeClassNames(
						cls,
						summary && styles.summaryColumn,
						// Mobile view: hide label & change font style
						j == 0 && styles.headerCell,
					);

					const cellContent = () => {
						if (summary && col.omitInSummary) {
							return null;
						}

						return (
							<>
								{useMobileLayout && isMobile && col.label && !col.disableMobileHeader && (
									<span className={styles.mobileHeader}>
										{typeof col.label === "string" ? col.label : <>{col.label}</>}
									</span>
								)}
								{summary && col.summaryRenderer !== undefined
									? col.summaryRenderer(value)
									: col.renderer(value, row, isExpanded, i)}
							</>
						);
					};

					return (
						<Td
							dataTh={typeof col.label === "string" ? col.label : ""}
							align={isMobile && col.alignOnMobile !== undefined ? col.alignOnMobile : col.align}
							className={cellClass}
							key={j}
							visibility={col.visibility}
							testId={`${col.testId}_${i}`}
						>
							{cellContent()}
						</Td>
					);
				})}
			</Tr>,
		);

		if (spec.expandedChildrenTableRender && isExpanded) {
			const item = spec.expandedChildrenTableRender(row, spec.level + 1, spec.columns.length);
			if (item) {
				rows.push(item);
			}
		}

		// eslint-disable-next-line react-hooks/immutability -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
		prev = row;
		return rows;
	};

	const tableRows = spec.data.map(renderRow());

	if (spec.summaryRow) {
		const summaryRow = generateSummaryRow(spec.data, spec.summaryRowKeys, spec.summaryOverride);
		tableRows.push(renderRow(true)(summaryRow, tableRows.length));
	}

	const makeMobileSortConfig = () =>
		spec.columns
			.filter(column => column.sortKey && column.label)
			.map(column => ({
				// These are defined at this point
				sortKey: column.sortKey || "",
				sortLabel: typeof column.label === "string" ? column.label : "", // Ensure sortLabel is a string
				active: spec.sortConfig?.sortProperty == column.sortKey,
			}));

	const tableClass = mergeClassNames(props.className, useMobileLayout && styles.mobile);

	if (spec.headless) {
		return (
			<Table className={tableClass} testId={props.testId}>
				<TBody>{tableRows}</TBody>
			</Table>
		);
	}

	const mobileHeader = useMobileLayout && spec.sortConfig && (
		<SorterHeadingMobile
			sortItems={makeMobileSortConfig()}
			config={spec.sortConfig}
			onUpdate={config => spec.sortConfig?.setSort(config.sortProperty, config.sortOrder)()}
			forceMobileLayout={props.forceMobileLayout}
		/>
	);

	return (
		<div className={mergeClassNames(isMobile && styles.isMobile)}>
			{mobileHeader}
			<Table className={tableClass} testId={props.testId}>
				<THead className={spec.headerClass}>
					<Tr>
						{spec.columns.map((col, i) => (
							<Fragment key={i}>
								{spec.sortConfig !== undefined && col.sortKey !== undefined && (
									<SorterHeading
										config={spec.sortConfig}
										sortKey={col.sortKey}
										align={isMobile && col.alignOnMobile !== undefined ? col.alignOnMobile : col.align}
										visibility={col.visibility}
										testId={`SorterHeading_${col.sortKey}`}
									>
										{typeof col.label === "string" ? col.label : <>{col.label}</>}
									</SorterHeading>
								)}
								{(spec.sortConfig === undefined || col.sortKey === undefined) && (
									<Th
										align={isMobile && col.alignOnMobile !== undefined ? col.alignOnMobile : col.align}
										visibility={col.visibility}
									>
										{typeof col.label === "string" ? col.label : <>{col.label}</>}
									</Th>
								)}
							</Fragment>
						))}
					</Tr>
				</THead>
				<TBody>{tableRows}</TBody>
			</Table>
		</div>
	);
};

export default SimpleTable;
